'use client';
/**
 * The master ALERTS toggle — sits directly under the "Top 15 Q" pill on
 * the 3D scatter overlay. Steve directive 2026-05-23: this is the kill
 * switch. Flip OFF if alerts get noisy; flip back ON to resume.
 *
 * Visual states:
 *  - ON: emerald dot + "ALERTS · 3 high · 5 med · 12 low" (live counts)
 *  - OFF: gray dot + "ALERTS OFF"
 *
 * Click anywhere on the pill cycles the state. Right-side label area is
 * also a click target for opening the drawer when ON (so the same button
 * pulls double duty: master toggle + drawer opener).
 */

import { useAlerts } from './AlertsContext';
import { useResource } from '@/lib/use-resource';
import type { ActionAlertsResponse } from '@/lib/action-types';

export function AlertsToggle() {
  const { alertsEnabled, setAlertsEnabled, openDrawer } = useAlerts();
  const { data } = useResource<ActionAlertsResponse>('/api/action-alerts', 60_000);

  const summary = data?.summary ?? { high: 0, medium: 0, low: 0, dismissed: 0, handled: 0 };
  const totalActive = summary.high + summary.medium + summary.low;
  const hasHigh = summary.high > 0;

  // iOS-style toggle: a track + sliding thumb. Click to flip. Label and
  // count chip live to the right of the switch; the count area is its
  // own click target that opens the drawer (so the switch flips on/off,
  // and the label opens the alert list). Steve directive 2026-05-23:
  // "I want a standard toggle on/off, not a click-the-pill."
  const trackOnBg = hasHigh ? 'bg-red-500/60' : 'bg-emerald-500/60';
  const trackBg = alertsEnabled ? trackOnBg : 'bg-[#1e293b]';

  return (
    <div
      className="flex items-center gap-2 bg-[#0a0e14] border border-[#1e293b] rounded-md px-2 py-1.5"
      title={alertsEnabled
        ? 'Action Monitor is ON. Flip the switch to silence dots/toasts/drawer (data layer keeps running in the background).'
        : 'Action Monitor is OFF. Flip the switch to resume surfacing alerts.'}
    >
      {/* The actual iOS-style toggle */}
      <button
        type="button"
        role="switch"
        aria-checked={alertsEnabled}
        aria-label="Toggle alerts"
        onClick={() => setAlertsEnabled(!alertsEnabled)}
        className={`relative inline-flex items-center w-10 h-5 rounded-full transition-colors duration-150 cursor-pointer ${trackBg} hover:opacity-90`}
      >
        <span
          className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-150 ${
            alertsEnabled ? 'translate-x-[1.375rem]' : 'translate-x-0.5'
          }`}
        />
      </button>

      {/* Label + counts. The label area is a button so clicking it opens
          the drawer (when ON and there are alerts to see). */}
      <button
        type="button"
        onClick={() => alertsEnabled && openDrawer(null)}
        className={`text-[11px] font-semibold tracking-wide transition-colors flex items-center gap-1.5 ${
          alertsEnabled
            ? 'text-[#cbd5e1] hover:text-white cursor-pointer'
            : 'text-[#64748b] cursor-default'
        }`}
        title={alertsEnabled && totalActive > 0 ? 'Open alerts drawer' : undefined}
      >
        <span className={alertsEnabled ? 'uppercase tracking-wide' : 'uppercase tracking-wide opacity-60'}>
          Alerts
        </span>
        {alertsEnabled ? (
          totalActive === 0 ? (
            <span className="text-[10px] text-emerald-300/80">all clear</span>
          ) : (
            <span className="text-[10px] inline-flex items-center gap-1">
              {summary.high > 0 && <span className="text-red-300">{summary.high} hi</span>}
              {summary.high > 0 && (summary.medium > 0 || summary.low > 0) && <span className="text-[#475569]">·</span>}
              {summary.medium > 0 && <span className="text-amber-300">{summary.medium} med</span>}
              {summary.medium > 0 && summary.low > 0 && <span className="text-[#475569]">·</span>}
              {summary.low > 0 && <span className="text-[#cbd5e1]">{summary.low} low</span>}
            </span>
          )
        ) : (
          <span className="text-[10px] text-[#475569]">off</span>
        )}
      </button>
    </div>
  );
}
